import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Tab, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tabs, TextField, Typography
} from '@mui/material';
import { extractErrorMessage } from '../Services/httpClient';
import { canEditCatalog, hasRole, ROLES } from '../Services/AuthService';
import { createItem, listItems } from '../Services/CatalogApi';
import { activeVersion, inFlightVersion, itemStatus, KEY_PATTERN, publishedGroupKeys } from '../Services/catalogModel';
import { newGroupContent, newTypeContent } from '../Services/contentModel';
import CatalogItemDialog from './CatalogItemDialog';
import TypeContentEditor from './TypeContentEditor';
import useCatalogLimits from './useCatalogLimits';

const ORANGE = '#f77513';
const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '');

const TABS = [
    { label: 'Tipos de notificação', match: (item) => item.kind === 'type' },
    { label: 'Grupos de limite', match: (item) => item.kind === 'group' },
    { label: 'Aguardando decisão', match: (item) => ['pending_approval', 'scheduled'].includes(inFlightVersion(item)?.state) }
];

const lastUpdate = (item) => item.versions.reduce((latest, v) => (v.updatedAt > latest ? v.updatedAt : latest), '');

/** Lista do catálogo de notificações: tipos, grupos de limite e a fila de aprovação. Cada linha abre o item. */
const CatalogPanel = ({ initialItemId = null, onInitialItemConsumed }) => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState(0);
    const [openId, setOpenId] = useState(null);
    const [creating, setCreating] = useState(null);   // { kind, key, content, summary }
    const [createError, setCreateError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const limits = useCatalogLimits();

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            setItems(await listItems());
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Um aviso do sino (ou um link `/?catalogo=<id>`) pede para abrir um item direto.
    useEffect(() => {
        if (!initialItemId) return;
        setOpenId(initialItemId);
        onInitialItemConsumed?.();
    }, [initialItemId]); // eslint-disable-line

    const rows = useMemo(() => items.filter(TABS[tab].match).sort((a, b) => a.key.localeCompare(b.key)), [items, tab]);
    const waiting = useMemo(() => items.filter(TABS[2].match).length, [items]);

    const startCreate = (kind) => {
        setCreating({ kind, key: '', content: kind === 'type' ? newTypeContent() : newGroupContent(), summary: '' });
        setCreateError(null);
    };

    const groupKeys = useMemo(() => publishedGroupKeys(items), [items]);

    const submitCreate = async () => {
        const key = creating.key.trim();
        if (!KEY_PATTERN.test(key)) {
            setCreateError('O id deve ter letras minúsculas, números, ponto, hífen ou sublinhado.');
            return;
        }
        setIsSaving(true);
        setCreateError(null);
        try {
            const version = await createItem({ kind: creating.kind, key, content: creating.content, summary: creating.summary || null });
            setCreating(null);
            await load();
            setOpenId(version.itemId);
        } catch (err) {
            setCreateError(extractErrorMessage(err));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 1100 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography variant="subtitle1" sx={{ color: ORANGE, fontWeight: 'bold', flexGrow: 1 }}>Catálogo de notificações</Typography>
                {hasRole(ROLES.consentEditor) && (
                    <Button variant="contained" style={{ backgroundColor: ORANGE, color: 'white' }} onClick={() => startCreate('type')}>Novo tipo</Button>
                )}
                {canEditCatalog() && (
                    <Button variant="outlined" style={{ color: ORANGE, borderColor: ORANGE }} onClick={() => startCreate('group')}>Novo grupo de limite</Button>
                )}
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} TabIndicatorProps={{ style: { backgroundColor: ORANGE } }}>
                <Tab label={TABS[0].label} />
                <Tab label={TABS[1].label} />
                <Tab label={<>{TABS[2].label}{waiting > 0 && <Chip size="small" color="warning" label={waiting} sx={{ ml: 1 }} />}</>} />
            </Tabs>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {isLoading ? <CircularProgress size={24} style={{ color: ORANGE }} /> : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '14px' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow style={{ backgroundColor: '#fff4ec' }}>
                                <TableCell>Id</TableCell>
                                <TableCell>Situação</TableCell>
                                <TableCell>Versão no ar</TableCell>
                                <TableCell>Última mudança</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={4} align="center" style={{ color: '#aaa', padding: '24px' }}>
                                    {tab === 2 ? 'Nada aguardando decisão' : 'Nenhum item ainda'}
                                </TableCell></TableRow>
                            )}
                            {rows.map((item) => {
                                const status = itemStatus(item);
                                const active = activeVersion(item);
                                return (
                                    <TableRow key={item.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenId(item.id)}>
                                        <TableCell sx={{ fontWeight: 600 }}>{item.key}</TableCell>
                                        <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
                                        <TableCell>{active && !item.isDeactivated ? `v${active.number}` : '—'}</TableCell>
                                        <TableCell>{formatDate(lastUpdate(item))}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {openId && <CatalogItemDialog itemId={openId} onClose={() => setOpenId(null)} onChanged={load} />}

            <Dialog open={!!creating} onClose={() => setCreating(null)} fullWidth maxWidth="lg">
                {creating && (
                    <>
                        <DialogTitle>{creating.kind === 'type' ? 'Novo tipo de notificação' : 'Novo grupo de limite'}</DialogTitle>
                        <DialogContent>
                            {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
                            <TextField label="Id estável" fullWidth size="small" sx={{ mt: 1, mb: 2 }} autoFocus value={creating.key}
                                helperText="Minúsculas, números, ponto, hífen e sublinhado. É o mesmo id que os produtores usam e não muda depois."
                                onChange={(e) => setCreating({ ...creating, key: e.target.value })} />
                            <TypeContentEditor kind={creating.kind} value={creating.content} groups={groupKeys} canEditConsent={hasRole(ROLES.consentEditor)} limits={limits}
                                onChange={(content) => setCreating((current) => ({ ...current, content }))} />
                            <TextField label="Resumo (opcional)" fullWidth size="small" sx={{ mt: 2 }} value={creating.summary}
                                onChange={(e) => setCreating({ ...creating, summary: e.target.value })} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setCreating(null)} style={{ color: '#777' }}>Cancelar</Button>
                            <Button onClick={submitCreate} disabled={isSaving || !creating.key.trim()} variant="contained" style={{ backgroundColor: ORANGE, color: 'white' }}>
                                Criar rascunho
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default CatalogPanel;
